import type { Puzzle } from "../puzzle/types.ts";
import { utcDateString } from "../puzzle/generator.ts";
import { APP_CONFIG } from "./config.ts";
import { resultSummary } from "./result-summary.ts";
import { formatElapsed, type PuzzleProgress } from "./storage.ts";

interface ImageShareTarget {
  canShare?: (data: ShareData) => boolean;
  share?: (data: ShareData) => Promise<void>;
}

export function puzzleShareUrl(date: string): string {
  const url = new URL("/", APP_CONFIG.siteUrl);
  url.searchParams.set("date", utcDateString(date));
  return url.href;
}

export function canShareImage(file: File, target: ImageShareTarget = navigator): boolean {
  try {
    return Boolean(target.share && target.canShare?.({ files: [file] }));
  } catch {
    return false;
  }
}

export async function shareImage(
  file: File,
  target: ImageShareTarget = navigator,
): Promise<"shared" | "cancelled" | "unavailable"> {
  const data: ShareData = { files: [file] };
  if (!canShareImage(file, target)) return "unavailable";
  return sendNativeShare(data, target);
}

export function shareLink(date: string, target: ImageShareTarget = navigator): Promise<"shared" | "cancelled" | "unavailable"> {
  return sendNativeShare({ url: puzzleShareUrl(date) }, target);
}

async function sendNativeShare(data: ShareData, target: ImageShareTarget): Promise<"shared" | "cancelled" | "unavailable"> {
  if (!target.share) return "unavailable";
  try {
    await target.share(data);
    return "shared";
  } catch (error) {
    if (error && typeof error === "object" && "name" in error && error.name === "AbortError") return "cancelled";
    throw error;
  }
}

export function shareText(puzzle: Puzzle, progress: PuzzleProgress, rememberedName = ""): string {
  const summary = resultSummary(puzzle, progress, rememberedName);
  const result = summary.solved ? String(summary.attempts) : "X";
  const rows = Array.from({ length: summary.attempts }, (_, index) =>
    summary.solved && index === summary.attempts - 1 ? "🟩🟩🟩" : "⬛⬛⬛",
  );

  return [
    `${APP_CONFIG.shortName} #${summary.puzzleNumber} ${result}/${summary.maxAttempts} · ${formatElapsed(summary.elapsedMs)}`,
    ...(summary.name ? [summary.name] : []),
    "",
    ...rows,
    "",
    puzzleShareUrl(summary.date),
  ].join("\n");
}

interface ImageClipboardTarget {
  write?: (items: ClipboardItem[]) => Promise<void>;
}

interface ImageClipboardItemType {
  new (items: Record<string, Blob>): ClipboardItem;
  supports?: (type: string) => boolean;
}

export function canCopyImage(
  clipboard: ImageClipboardTarget | undefined = navigator.clipboard,
  itemType: ImageClipboardItemType | undefined = globalThis.ClipboardItem,
): boolean {
  try {
    return Boolean(clipboard?.write && itemType && (!itemType.supports || itemType.supports("image/png")));
  } catch {
    return false;
  }
}

export async function copyImage(
  file: Blob,
  clipboard: ImageClipboardTarget | undefined = navigator.clipboard,
  itemType: ImageClipboardItemType | undefined = globalThis.ClipboardItem,
): Promise<void> {
  if (!canCopyImage(clipboard, itemType)) throw new Error("Image copying is not supported in this browser.");
  if (file.type !== "image/png") throw new Error("The result image must be a PNG.");
  const png = new Blob([file], { type: "image/png" });
  await clipboard!.write!([new itemType!({ "image/png": png })]);
}

export async function copyResult(
  text: string,
  clipboard: { writeText?: (value: string) => Promise<void> } | undefined = navigator.clipboard,
  fallback: (value: string) => boolean = copySelectedText,
): Promise<void> {
  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(text);
      return;
    } catch {
      if (fallback(text)) return;
      throw new Error("Text copying was blocked by this browser.");
    }
  }
  if (!fallback(text)) throw new Error("Text copying is not supported in this browser.");
}

function copySelectedText(text: string): boolean {
  const focused = document.activeElement;
  const selection = window.getSelection();
  const ranges = selection ? Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange()) : [];
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.tabIndex = -1;
  textarea.className = "copy-fallback";
  document.body.append(textarea);
  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
    selection?.removeAllRanges();
    ranges.forEach((range) => selection?.addRange(range));
    if (focused instanceof HTMLElement) focused.focus({ preventScroll: true });
  }
}